import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { FileService } from './file.service';
import { Capacitor } from '@capacitor/core';
import * as Sentry from '@sentry/angular';
import { FilesHelperService } from './files-helper.service';

export interface DocumentDownloadState {
  isDownloading: boolean;
  documentId: number | null;
}

@Injectable({
  providedIn: 'root'
})
export class DocumentDownloadService {
  private downloadStateMap = new Map<
    number,
    BehaviorSubject<DocumentDownloadState>
  >();

  constructor(
    private fileService: FileService,
    private filesHelper: FilesHelperService
  ) {}

  getDownloadState(documentId: number): BehaviorSubject<DocumentDownloadState> {
    if (!this.downloadStateMap.has(documentId)) {
      this.downloadStateMap.set(
        documentId,
        new BehaviorSubject<DocumentDownloadState>({
          isDownloading: false,
          documentId
        })
      );
    }
    return this.downloadStateMap.get(documentId)!;
  }

  async downloadDocument(documentId: number, fileName: string): Promise<void> {
    const state = this.getDownloadState(documentId);
    if (state.value.isDownloading) {
      return;
    }

    try {
      state.next({ ...state.value, isDownloading: true });
      const filePath = await this.fileService.downloadFileForDeal(
        documentId,
        fileName,
        undefined
      );

      // Only try to open it if we're on a native platform
      if (filePath && Capacitor.isNativePlatform()) {
        const extension = fileName.toLowerCase().split('.').pop() || '';
        const mimeType = this.filesHelper.getMimeTypeFromExtension(extension);
        await this.filesHelper.openFile(filePath, mimeType);
      }
      // For web platform, the file is already downloaded by downloadFileForDeal
    } catch (error) {
      Sentry.captureException(error);
    } finally {
      state.next({ ...state.value, isDownloading: false });
    }
  }
}
